import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NoteBlank, Sparkle, ChatText } from 'phosphor-react';
import ChatTile from '../components/ChatTile';
import ConversationDetail from './ConversationDetail';
import { OrdersTray } from './OrdersTray';
import { useApi } from '../hooks/useApi';
import { Conversation, UnifiedThread, WhatsAppThread } from '../types/api';
import { Button } from '../components/Button';
import { MessagesPageLayout, ThreadList } from '../components/messages';

type ChannelType = 'all' | 'whatsapp' | 'email';

interface AllChatsProps {
  onBack?: () => void;
  onChatClick?: (conversation: Conversation) => void;
  onMenuClick?: () => void;
  onNavigateToOrder?: (orderId: string) => void;
}

// Map a UnifiedThread to the Conversation shape that ChatTile + ConversationDetail expect.
// We use the thread's composite id string (e.g. "wa_5", "em_3") as the conversation `id`
// cast to number via a hack — instead we store it in a threadKey extra field and keep the
// numeric id for API calls. The URL uses threadKey to avoid wa/em collisions.
function threadToConversation(thread: UnifiedThread): Conversation & { threadKey: string; emailIntakeId?: number } {
  if (thread.type === 'whatsapp') {
    return {
      id: thread.conversationId,
      threadKey: thread.id,
      customerPhone: thread.customerPhone,
      customerName: thread.customerName,
      lastMessage: thread.lastMessage,
      lastMessageAt: thread.lastMessageAt,
      status: thread.status as any,
      context: thread.context,
      channel: 'whatsapp',
      orderId: thread.orderId,
      orderStatus: thread.orderStatus as any,
    } as any;
  }
  return {
    id: thread.emailIntakeId,
    threadKey: thread.id,
    emailIntakeId: thread.emailIntakeId,
    customerPhone: thread.customerEmail,
    customerName: thread.customerName,
    lastMessage: thread.subject,
    lastMessageAt: thread.lastMessageAt,
    status: 'active' as any,
    channel: 'email',
    orderId: thread.orderId,
    orderStatus: thread.orderStatus as any,
  } as any;
}

export const AllChats: React.FC<AllChatsProps> = ({ onBack, onChatClick, onMenuClick, onNavigateToOrder }) => {
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const [searchValue, setSearchValue] = useState('');
  const [threads, setThreads] = useState<UnifiedThread[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);
  const [showManualModeBottomSheet, setShowManualModeBottomSheet] = useState(false);
  const [pendingConversationId, setPendingConversationId] = useState<number | null>(null);
  const [activeChannel, setActiveChannel] = useState<ChannelType>('all');
  const [orderModalId, setOrderModalId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch unified threads from API
  useEffect(() => {
    if (api.isLoading || !api.isAuthenticated) return;

    const fetchThreads = async () => {
      try {
        setIsLoading(true);
        const data = await api.getThreads();
        setThreads(data);
        setConversations(data.map(threadToConversation));
        setError(null);
      } catch (err) {
        console.error('AllChats: Error fetching threads:', err);
        setError('Error al cargar los chats. Por favor, intenta nuevamente.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchThreads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.isAuthenticated, api.isLoading]);

  // Check if we're on mobile (for conditional rendering)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Sync selectedConversation with URL conversationId (for master-detail desktop mode)
  // URL uses the threadKey ("wa_5", "em_3") to avoid id collisions between channels
  useEffect(() => {
    if (!urlConversationId || conversations.length === 0) {
      if (location.pathname === '/chats' && !isMobile) {
        setSelectedConversation(null);
      }
      return;
    }
    const conversation = conversations.find(c =>
      (c as any).threadKey === urlConversationId || c.id.toString() === urlConversationId
    );
    if (conversation) {
      setSelectedConversation(conversation);
    } else {
      setSelectedConversation(null);
    }
  }, [urlConversationId, conversations, location.pathname, isMobile]);

  // Filter by search and channel
  const filteredConversations = conversations.filter(conversation => {
    const searchLower = searchValue.toLowerCase();
    const customerName = conversation.customerName || '';
    const phone = conversation.customerPhone || '';
    const lastMessage = conversation.lastMessage || '';

    const matchesSearch =
      customerName.toLowerCase().includes(searchLower) ||
      phone.toLowerCase().includes(searchLower) ||
      lastMessage.toLowerCase().includes(searchLower);

    const channel = conversation.channel || 'whatsapp';
    const matchesChannel = activeChannel === 'all' || channel === activeChannel;

    return matchesSearch && matchesChannel;
  });

  const handleChatClick = (conversation: Conversation) => {
    const isMobileNow = window.innerWidth < 768;
    const key = (conversation as any).threadKey || conversation.id.toString();
    if (isMobileNow) {
      navigate(`/chats/${key}`);
      if (onChatClick) onChatClick(conversation);
    } else {
      setSelectedConversation(conversation);
      navigate(`/chats/${key}`, { replace: true });
    }
  };

  const handleBackFromConversation = () => {
    setSelectedConversation(null);
    navigate('/chats', { replace: true });
  };

  // Bulk actions (WhatsApp only)
  const handleBulkActivateAI = async () => {
    setIsProcessingBulkAction(true);
    setShowMenu(false);
    try {
      const waThreads = threads.filter(t => t.type === 'whatsapp') as WhatsAppThread[];
      await Promise.all(waThreads.map(t => api.setManualMode(t.conversationId, false)));
      const data = await api.getThreads();
      setThreads(data);
      setConversations(data.map(threadToConversation));
    } catch (err) {
      console.error('Error activating AI for all chats:', err);
      setError('Error al activar atención automática para todos los chats');
    } finally {
      setIsProcessingBulkAction(false);
    }
  };

  const handleBulkDeactivateAI = async () => {
    setIsProcessingBulkAction(true);
    setShowMenu(false);
    try {
      const waThreads = threads.filter(t => t.type === 'whatsapp') as WhatsAppThread[];
      await Promise.all(waThreads.map(t => api.setManualMode(t.conversationId, true)));
      const data = await api.getThreads();
      setThreads(data);
      setConversations(data.map(threadToConversation));
    } catch (err) {
      console.error('Error deactivating AI for all chats:', err);
      setError('Error al desactivar atención automática para todos los chats');
    } finally {
      setIsProcessingBulkAction(false);
    }
  };

  const handleConversationModeToggle = async (conversationId: number, isManual: boolean) => {
    try {
      await api.setManualMode(conversationId, isManual);
      const data = await api.getThreads();
      setThreads(data);
      const updated = data.map(threadToConversation);
      setConversations(updated);
      if (selectedConversation?.id === conversationId) {
        const upd = updated.find(c => c.id === conversationId);
        if (upd) setSelectedConversation(upd);
      }
    } catch (err) {
      console.error('Error toggling conversation mode:', err);
      setError('Error al cambiar el modo de la conversación');
    }
  };

  const handleManualModeRequest = (conversationId: number) => {
    setPendingConversationId(conversationId);
    setShowManualModeBottomSheet(true);
  };

  const confirmManualModeChange = async () => {
    if (pendingConversationId !== null) {
      setShowManualModeBottomSheet(false);
      await handleConversationModeToggle(pendingConversationId, true);
      setPendingConversationId(null);
    }
  };

  const handleConversationUpdate = async (updatedConversation: Conversation) => {
    setConversations(prev => prev.map(conv =>
      conv.id === updatedConversation.id ? updatedConversation : conv
    ));
    if (selectedConversation?.id === updatedConversation.id) {
      setSelectedConversation(updatedConversation);
    }
  };

  const emptyState = (
    <div className="flex flex-col gap-3 items-center justify-center py-16 px-4 w-full">
      <NoteBlank size={48} weight="bold" className="text-muted-foreground" />
      <p className="font-sans font-medium text-base text-text-subtle text-center">
        No hay conversaciones disponibles
      </p>
    </div>
  );

  const errorState = (
    <div className="flex flex-col gap-3 w-full p-2">
      {/* Sample chat 1 */}
      <div className="p-3 rounded-lg bg-white border border-border hover:bg-accent/5 cursor-pointer transition-colors">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <p className="font-medium text-text">Juan García</p>
            <p className="text-sm text-text-subtle">+56 9 8765 4321</p>
          </div>
          <span className="text-xs text-text-subtle">9:30 AM</span>
        </div>
        <p className="text-sm text-text-subtle truncate">Hola, me interesa conocer más sobre los productos...</p>
      </div>

      {/* Sample chat 2 */}
      <div className="p-3 rounded-lg bg-white border border-border hover:bg-accent/5 cursor-pointer transition-colors">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <p className="font-medium text-text">María Rodríguez</p>
            <p className="text-sm text-text-subtle">+56 9 9876 5432</p>
          </div>
          <span className="text-xs text-text-subtle">2:15 PM</span>
        </div>
        <p className="text-sm text-text-subtle truncate">¿Cuál es el precio de envío a Santiago?</p>
      </div>

      {/* Sample chat 3 */}
      <div className="p-3 rounded-lg bg-white border border-border hover:bg-accent/5 cursor-pointer transition-colors">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <p className="font-medium text-text">Carlos López</p>
            <p className="text-sm text-text-subtle">+56 9 7654 3210</p>
          </div>
          <span className="text-xs text-text-subtle">5:45 PM</span>
        </div>
        <p className="text-sm text-text-subtle truncate">Necesito hacer un pedido urgente para mañana</p>
      </div>
    </div>
  );
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // On mobile, if we resize to desktop and have a selected conversation, keep it
      // On desktop, if we resize to mobile and have a selected conversation, navigate away
      if (window.innerWidth >= 768 && selectedConversation && onChatClick) {
        // On desktop, don't navigate
      } else if (window.innerWidth < 768 && selectedConversation) {
        // On mobile, if we have a selected conversation, navigate to it
        if (onChatClick) {
          onChatClick(selectedConversation);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedConversation, onChatClick]);

  // Close menu when clicking outside (only for desktop dropdown)
  useEffect(() => {
    if (!showMenu || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, isMobile]);

  const bulkActionsMenu = (
    <div className="py-1">
      <Button
        variant="ghost"
        onClick={handleBulkActivateAI}
        disabled={isProcessingBulkAction}
        className="w-full justify-start"
        leftIcon={<Sparkle weight="bold" size={18} />}
      >
        Activar atención para todos los chats
      </Button>
      <Button
        variant="ghost"
        onClick={handleBulkDeactivateAI}
        disabled={isProcessingBulkAction}
        className="w-full justify-start"
        leftIcon={<ChatText weight="bold" size={18} />}
      >
        Desactivar atención para todos los chats
      </Button>
    </div>
  );

  const threadsList = (
    <AnimatePresence>
      {filteredConversations.map((conversation, index) => (
        <motion.div
          key={conversation.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.5,
            delay: index * 0.02,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          className="w-full min-w-0 max-w-full"
        >
          <ChatTile
            conversation={conversation}
            onClick={() => handleChatClick(conversation)}
            onModeToggle={handleConversationModeToggle}
            onManualModeRequest={handleManualModeRequest}
            onOrderBadgeClick={(orderId) => {
              setOrderModalId(`P-${orderId}`);
            }}
            className={selectedConversation?.id === conversation.id ? 'bg-accent/5' : ''}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  );

  const desktopEmptyState = (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex flex-col gap-3 items-center justify-center py-16 px-4">
        <NoteBlank size={48} weight="bold" className="text-muted-foreground" />
        <p className="font-sans font-medium text-base text-text-subtle text-center">
          Selecciona una conversación para ver los mensajes
        </p>
      </div>
    </div>
  );

  return (
    <>
      <MessagesPageLayout
        sidebar={
          <ThreadList
            title="Conversaciones"
            threads={threadsList}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            isLoading={isLoading}
            isEmpty={!isLoading && !error && filteredConversations.length === 0}
            emptyState={emptyState}
            onMenuButtonClick={() => setShowMenu(!showMenu)}
            menuRef={menuRef}
            showMenu={showMenu && !isMobile}
            isProcessingBulkAction={isProcessingBulkAction}
            actions={bulkActionsMenu}
            showChannelFilter={true}
            activeChannel={activeChannel}
            onChannelChange={setActiveChannel}
            whatsappCount={conversations.filter(c => (c.channel || 'whatsapp') === 'whatsapp').length}
            emailCount={conversations.filter(c => c.channel === 'email').length}
          />
        }
        detail={
          selectedConversation ? (
            <ConversationDetail
              conversationId={selectedConversation.id.toString()}
              conversation={selectedConversation}
              onBack={handleBackFromConversation}
              onNavigateToOrder={onNavigateToOrder}
              onMenuClick={onMenuClick}
              isMasterDetail={true}
              onConversationUpdate={handleConversationUpdate}
            />
          ) : (
            desktopEmptyState
          )
        }
        isMobile={isMobile}
        onMenuClick={onMenuClick}
      />

      {orderModalId && (
        <div style={{ position: 'fixed', width: 0, height: 0, overflow: 'visible', zIndex: 0 }}>
          <OrdersTray
            initialOrderId={orderModalId}
            onInitialOrderClose={() => setOrderModalId(null)}
          />
        </div>
      )}

      {/* Bottom Sheet for Mobile - Bulk Actions */}
      {showMenu && isMobile && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowMenu(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <motion.div
            className="relative bg-background w-full rounded-t-[24px]"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-[460px] mx-auto p-6">
              <div className="mx-auto mb-4 w-12 h-1 bg-gray-300 rounded-full" />
              <h3 className="font-sans font-semibold text-xl text-text mb-6">
                Sobre los chats
              </h3>
              <div className="flex flex-col gap-2">
                {bulkActionsMenu}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Manual Mode Confirmation Bottom Sheet */}
      {showManualModeBottomSheet && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowManualModeBottomSheet(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <motion.div
            className="relative bg-background w-full rounded-t-[24px]"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-[460px] mx-auto p-6">
              <div className="mx-auto mb-4 w-12 h-1 bg-gray-300 rounded-full" />
              <h3 className="font-sans font-semibold text-xl text-text mb-2">
                Desactivar chat automático con IA
              </h3>
              <p className="font-sans font-normal text-sm text-text-subtle mb-6">
                Para poder escribir se desactiva la atención automatizada con IA, pero puedes volver a activarla con los botones de abajo a la izquierda.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  fullWidth
                  onClick={async () => {
                    setShowManualModeBottomSheet(false);
                    await handleConversationModeToggle(pendingConversationId!, false);
                  }}
                  className="py-4 rounded-[6px]"
                >
                  Desactivar chat automático
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  onClick={() => {
                    setShowManualModeBottomSheet(false);
                    setPendingConversationId(null);
                  }}
                  className="py-3"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default AllChats;

